import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { Input } from "@/components/ui/input";
import { KeyRound, CheckCircle2, AlertTriangle, RefreshCw, Send, AtSign } from "lucide-react";

// Oct 2026 — extracted from PWSPartnerNetwork.jsx so the generic Admin →
// Partners page can offer the exact same Trade Portal invite/resend flow
// for every portal-eligible partner type, not just PWS Franchisees. Logic
// is unchanged — only the data-testid prefix moved from "pws-" to the
// generic "trade-portal-" since this is no longer PWS-only.
export function TradePortalInvitePanel({ partnerId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [orphanPrompt, setOrphanPrompt] = useState(null); // { existing_user_id, existing_user_role } | null
  const [emailDialog, setEmailDialog] = useState(null);   // { new_email: string } | null
  const [emailBusy, setEmailBusy] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/trade-portal-invite-status`);
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, [partnerId]);

  const send = async (linkExistingUser = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/partners/${partnerId}/trade-portal-invite`,
        { link_existing_user: !!linkExistingUser },
      );
      if (data.ok) {
        toast.success(
          data.is_resend
            ? `Trade Portal invite resent (attempt #${data.invite_count}).`
            : "Trade Portal invite sent — partner can now set their password."
        );
      } else {
        toast.error(`Send failed: ${data.send_error || data.send_status}`);
      }
      setOrphanPrompt(null);
      await fetchStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "existing_user_orphan") {
        setOrphanPrompt({
          existing_user_id:   detail.existing_user_id,
          existing_user_role: detail.existing_user_role,
        });
      } else {
        const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Failed to send invite";
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="trade-portal-invite-panel-loading"
        className="mt-5 rounded-2xl border border-sky-200 bg-sky-50/50 p-4 text-xs text-slate-500"
      >
        Loading Trade Portal invite status…
      </div>
    );
  }

  if (!status) return null;

  // Bail early for partner types that don't have portal access. The
  // partner detail drawer for direct-SCWT-only trades would render an
  // empty panel otherwise, which is noise for the admin.
  if (!status.partner_type_eligible) {
    return (
      <div
        data-testid="trade-portal-invite-panel-ineligible"
        className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
          Trade Portal invite
        </div>
        <div className="text-xs text-slate-500">
          Partner type <code className="text-[10px] bg-slate-100 px-1 rounded">{status.partner_type || "—"}</code> does not have portal access yet.
        </div>
      </div>
    );
  }

  const alreadySent = !!status.trade_portal_invite_sent_at;
  const isSent = status.trade_portal_invite_status === "sent";
  const isFailed = status.trade_portal_invite_status && status.trade_portal_invite_status !== "sent";
  const noEmail = !status.email;

  return (
    <div
      data-testid="trade-portal-invite-panel"
      className="mt-5 rounded-2xl border border-sky-200 bg-sky-50/50 p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-700 flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Trade Portal invite
          </div>
          <div className="text-xs text-slate-600 mt-1 leading-relaxed max-w-[42ch]">
            Provisions a login for this partner and emails them a secure 7-day password-setup link.
          </div>
        </div>
        {alreadySent && isSent && (
          <span
            data-testid="trade-portal-invite-status-sent"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 whitespace-nowrap"
          >
            <CheckCircle2 className="w-3 h-3" /> Invite sent
          </span>
        )}
        {isFailed && (
          <span
            data-testid="trade-portal-invite-status-failed"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 whitespace-nowrap"
          >
            <AlertTriangle className="w-3 h-3" /> Send failed
          </span>
        )}
      </div>

      {alreadySent && (
        <div className="rounded-lg bg-white border border-slate-200 p-2.5 text-[11px] text-slate-600 mt-1 space-y-0.5">
          <div>
            First sent: <span className="font-semibold text-slate-900">{formatDateTimeAU(status.trade_portal_invite_sent_at)}</span>
            {status.trade_portal_invite_sent_by && (
              <> by <code className="text-[10px] bg-slate-100 px-1 rounded">{status.trade_portal_invite_sent_by}</code></>
            )}
          </div>
          {status.trade_portal_invite_resent_at && (
            <div>
              Last resend: <span className="font-semibold text-slate-900">{formatDateTimeAU(status.trade_portal_invite_resent_at)}</span>
              {" · "}count: <span className="font-semibold">{status.trade_portal_invite_count}</span>
            </div>
          )}
          {status.trade_portal_invite_last_error && (
            <div className="text-rose-700">Last error: {status.trade_portal_invite_last_error}</div>
          )}
        </div>
      )}

      {status.partner_blocked && (
        <div
          data-testid="trade-portal-invite-blocked-warning"
          className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900"
        >
          Partner status is <strong>{status.partner_status}</strong> — unpause / restore before sending the invite.
        </div>
      )}
      {noEmail && (
        <div
          data-testid="trade-portal-invite-no-email-warning"
          className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900"
        >
          No email on file — edit the partner and add one before sending an invite.
        </div>
      )}

      {/* Feb 2026 — Disabled-user warning. Fires when the linked users
          row is currently disabled. Sending an invite in that state
          just gets the partner to "Your account has been disabled"
          after they've reset their password — so we surface it up-
          front and disable the Send button below. */}
      {status.linked_user_disabled && (
        <div
          data-testid="trade-portal-invite-user-disabled-warning"
          className="mt-3 rounded-lg bg-rose-50 border border-rose-300 p-3 text-[11px] text-rose-900"
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Linked user is DISABLED
          </div>
          <div className="leading-relaxed">
            The Trade Portal user tied to this partner
            (<code className="text-[10px] bg-white px-1 rounded">{status.linked_user_email || "—"}</code>)
            currently has <code className="text-[10px] bg-white px-1 rounded">status={status.linked_user_status || "disabled"}</code>.
            Any invite/resend will land at a login screen showing
            &quot;Your account has been disabled.&quot; Re-enable the row in
            {" "}<a href="/admin/users" className="underline font-semibold">/admin/users</a>{" "}
            first, then retry.
          </div>
        </div>
      )}

      {/* Feb 2026 — Contact vs Login email split display. Both fields
          exist independently on purpose (the partner may want a
          separate public contact address). Show them side-by-side
          so the admin never wonders which is which. */}
      {status.linked_user_email && status.email &&
       status.linked_user_email.toLowerCase() !== status.email.toLowerCase() && (
        <div
          data-testid="trade-portal-invite-email-split-info"
          className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-700"
        >
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contact email (public)</div>
              <div className="font-mono text-slate-900 mt-0.5 break-all">{status.email}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Login email (portal)</div>
              <div className="font-mono text-slate-900 mt-0.5 break-all">{status.linked_user_email}</div>
            </div>
          </div>
          <div className="mt-1.5 text-[10px] text-slate-500">
            These addresses can differ safely. Resends go to the login email.
          </div>
        </div>
      )}

      {/* Feb 2026 — Collision chip: warn when the linked users row's
          `full_name` doesn't match the partner's contact_name /
          business_name. Common cause: attaching an existing orphan
          user (e.g. a former Lab Tech) — the invite service now syncs
          full_name on attach, but pre-existing partners can still
          drift, and the collision-detect is cheap. */}
      {(() => {
        const expected = (status.partner_contact_name || status.partner_business_name || "").trim();
        const actual = (status.linked_user_full_name || "").trim();
        if (!status.linked_user_full_name || !expected) return null;
        if (expected.toLowerCase() === actual.toLowerCase()) return null;
        return (
          <div
            data-testid="trade-portal-invite-name-collision-warning"
            className="mt-3 rounded-lg bg-amber-50 border border-amber-300 p-3 text-[11px] text-amber-900"
          >
            <div className="font-semibold mb-1 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Login name mismatch
            </div>
            <div className="leading-relaxed">
              The linked Trade Portal login shows <code className="text-[10px] bg-white px-1 rounded">{actual || "—"}</code> in
              the header, but this partner&apos;s contact name is
              {" "}<code className="text-[10px] bg-white px-1 rounded">{expected}</code>.
              Resending the invite or using <em>Reset portal role</em> will sync the display name.
            </div>
            {status.linked_user_email && (
              <div className="mt-1 text-[10px] text-amber-800">
                Login row: <code className="text-[10px] bg-white px-1 rounded">{status.linked_user_email}</code>
              </div>
            )}
          </div>
        );
      })()}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={busy || status.partner_blocked || noEmail || status.linked_user_disabled}
          onClick={() => send(false)}
          data-testid={alreadySent ? "trade-portal-invite-resend-btn" : "trade-portal-invite-send-btn"}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
          title={status.linked_user_disabled ? "Linked user is disabled — re-enable in /admin/users first" : undefined}
        >
          {alreadySent ? <RefreshCw className="w-3 h-3" /> : <Send className="w-3 h-3" />}
          {busy ? "Sending…" : alreadySent ? "Resend invite" : "Send Trade Portal invite"}
        </button>
      </div>

      {orphanPrompt && (
        <div
          data-testid="trade-portal-invite-orphan-prompt"
          className="mt-3 rounded-lg bg-amber-50 border border-amber-300 p-3 text-[11px] text-amber-900"
        >
          <div className="font-semibold mb-1">Existing account with this email</div>
          <div className="leading-relaxed">
            A user row already exists for <code className="text-[10px] bg-white px-1 rounded">{status.email}</code>
            {" "}(role <code className="text-[10px] bg-white px-1 rounded">{orphanPrompt.existing_user_role || "—"}</code>)
            but is not linked to any partner. Attach it to this partner and send the invite?
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => send(true)}
              data-testid="trade-portal-invite-orphan-confirm-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-amber-600 hover:bg-amber-700 text-white"
            >
              Attach and send
            </button>
            <button
              type="button"
              onClick={() => setOrphanPrompt(null)}
              data-testid="trade-portal-invite-orphan-cancel-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Feb 2026 — Deliberately-separate "Update login email" action.
          Never fires from the general partner-edit form so admins
          can't bump a Trade Portal login email by accident. Only
          rendered when a portal user is actually linked. */}
      {status.trade_portal_user_id && (
        <div className="mt-3 pt-3 border-t border-sky-100">
          {/* Feb 2026 — always use the linked user row's actual email
              as the current portal login. `status.email` is the
              PARTNER CONTACT email (deliberately separate). Falling
              back to `status.email` only when the linked field is
              missing keeps old records rendering safely. */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-slate-600 leading-snug flex items-center gap-1.5">
              <AtSign className="w-3 h-3 text-slate-400" />
              Login email:
              <code
                data-testid="trade-portal-current-login-email"
                className="text-[10px] bg-slate-100 px-1 py-0.5 rounded"
              >{status.linked_user_email || status.email}</code>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setEmailDialog({ new_email: status.linked_user_email || status.email || "" })}
                data-testid="trade-portal-update-login-email-btn"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-sky-300 text-sky-700 hover:bg-sky-50"
              >
                Update login email
              </button>
              {/* Feb 2026 (partner-login-routing fix): repair action for
                  partners whose linked user has drifted off role="partner"
                  (typically after an old orphan-attach kept a
                  lab_technician role). Small, always visible when a
                  linked user exists — clicking it is idempotent when the
                  role is already correct. */}
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(
                    "Force this partner's portal login back to role=partner? This is safe to run even when the role is already correct."
                  )) return;
                  try {
                    const { data } = await api.post(
                      `/admin/partners/${partnerId}/reset-portal-user-role`,
                    );
                    if (data.from_role && data.from_role !== data.to_role) {
                      toast.success(`Portal login role reset: ${data.from_role} → ${data.to_role}.`);
                    } else {
                      toast.success("Portal login role is already correct.");
                    }
                    await fetchStatus();
                  } catch (err) {
                    const detail = err?.response?.data?.detail;
                    toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Reset failed");
                  }
                }}
                data-testid="trade-portal-reset-role-btn"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Force role=partner on the linked login (idempotent)"
              >
                Fix login role
              </button>
            </div>
          </div>
          <p className="mt-1 text-[10px] text-slate-500 leading-snug">
            This is separate from the partner CONTACT email. Editing the contact email in this drawer will NOT move the login.
          </p>
        </div>
      )}

      {emailDialog && (
        <div
          data-testid="trade-portal-update-login-email-dialog"
          className="mt-3 rounded-lg bg-white border border-sky-300 p-3 text-[11px] text-slate-800 shadow-sm"
        >
          <div className="font-semibold mb-1 text-slate-900">Update Trade Portal login email</div>
          <p className="text-slate-600 leading-snug mb-2">
            The partner will need to use the new email to log in from now on. Their existing password stays the same. Any pending password-setup tokens remain valid.
          </p>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">New login email</label>
          <Input
            data-testid="trade-portal-update-login-email-input"
            type="email"
            value={emailDialog.new_email}
            onChange={(e) => setEmailDialog({ ...emailDialog, new_email: e.target.value })}
            className="mt-1 text-xs h-8"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={emailBusy || !emailDialog.new_email || emailDialog.new_email.toLowerCase() === (status.linked_user_email || status.email || "").toLowerCase()}
              onClick={async () => {
                setEmailBusy(true);
                try {
                  const { data } = await api.post(
                    `/admin/partners/${partnerId}/trade-portal-login-email`,
                    { new_email: emailDialog.new_email.trim().toLowerCase(), confirm: true },
                  );
                  if (data.changed === false) {
                    toast.info("Login email unchanged.");
                  } else {
                    const superseded = data.superseded_token_count || 0;
                    toast.success(
                      superseded > 0
                        ? `Login email updated to ${data.new_email}. ${superseded} pending setup/reset link${superseded === 1 ? "" : "s"} invalidated.`
                        : `Login email updated to ${data.new_email}.`
                    );
                  }
                  setEmailDialog(null);
                  await fetchStatus();
                } catch (err) {
                  const detail = err?.response?.data?.detail;
                  const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Failed to update login email";
                  toast.error(msg);
                } finally {
                  setEmailBusy(false);
                }
              }}
              data-testid="trade-portal-update-login-email-confirm-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40"
            >
              {emailBusy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEmailDialog(null)}
              data-testid="trade-portal-update-login-email-cancel-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
